import { Box, Collapse, Stack } from '@mui/material'
import { ReactComponent as Arrow } from 'assets/arrow_open_logo.svg'
import verifiedIcon from 'assets/Verifide.png'
import {
	formatExaminationPeriodicityShort,
	formatTreatmentDuration,
	getCombinedMilkRejectPeriod,
	getExaminationInterval,
	getMaxTreatmentDuration,
	getMilkRejectPeriod,
	getTreatmentExaminationDates,
	startOfDay,
} from 'components/cattles/treatmentPage/utils/treatmentSchemaDisplay'
import { morphForWords } from 'components/shared/processes'
import { ITreatmentSchema } from 'models/ICattlesData'
import moment from 'moment'
import { useState } from 'react'
import { COLORS } from 'styles/colors'
import { TypographyHeader14 } from 'ui/typography/TypographyHeader14'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographyPrimary16Bold } from 'ui/typography/TypographyPrimary16Bold'
import { TypographySecondary } from 'ui/typography/TypographySecondary'

type TreatmentSchemaDetailsProps = {
	schemas: ITreatmentSchema[]
}

const DetailRow = ({ label, value }: { label: string; value: string }) => (
	<TypographyPrimary>
		{label}: {value}
	</TypographyPrimary>
)

const InfoBlock = ({
	title,
	children,
}: {
	title: string
	children: string
}) => (
	<Stack gap='8px'>
		<TypographyHeader14>{title}</TypographyHeader14>
		<TypographyPrimary>{children}</TypographyPrimary>
	</Stack>
)

const formatMilkRejectLabel = (period: {
	start: number
	end: number
	days: number
}) =>
	`С ${moment(period.start).format('DD.MM.YYYY')} по ${moment(period.end).format('DD.MM.YYYY')}, ${period.days} ${morphForWords(period.days, ['день', 'дня', 'дней'])}.`

const SchemaDetails = ({
	schema,
	totalDuration,
	milkReject,
	showSummary,
}: {
	schema: ITreatmentSchema
	totalDuration: number | null
	milkReject: ReturnType<typeof getMilkRejectPeriod>
	showSummary: boolean
}) => {
	const [isDescriptionOpen, setIsDescriptionOpen] = useState(true)
	const today = startOfDay(Date.now())
	const interval = getExaminationInterval(schema)
	const examDates = getTreatmentExaminationDates(
		today,
		schema.duration,
		interval,
	)
	const firstExam = examDates[0]
	const lastExam = examDates[examDates.length - 1]
	const examLabel = !firstExam
		? '-'
		: firstExam === lastExam
			? moment(firstExam).format('DD.MM')
			: `${moment(firstExam).format('DD.MM')} → ${moment(lastExam).format('DD.MM')}`
	const description = schema.description?.trim()
	const hasAccordion = Boolean(description || milkReject || showSummary)

	return (
		<Stack gap='12px' sx={{ minWidth: 0, maxWidth: '100%' }}>
			<TypographyPrimary16Bold sx={{ overflowWrap: 'anywhere' }}>
				Схема лечения: {schema.title?.trim() || `Схема лечения ${schema.id}`}
			</TypographyPrimary16Bold>
			<Stack gap='4px'>
				<DetailRow
					label='Продолжительность'
					value={formatTreatmentDuration(schema.duration)}
				/>
				<DetailRow label='Осмотры' value={examLabel} />
				<DetailRow
					label='Периодичность'
					value={formatExaminationPeriodicityShort(interval)}
				/>
			</Stack>
			{examDates.length > 0 && (
				<Box display='flex' flexWrap='wrap' gap='8px'>
					{examDates.map(date => {
						const isToday = date === today
						const dateLabel = moment(date).format('DD.MM.YY')
						return (
							<Box
								key={date}
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: '6px',
									padding: '8px 12px',
									borderRadius: '12px',
									border: 'none',
									backgroundColor: COLORS.background,
								}}
							>
								<TypographyPrimary>
									{isToday ? `Сегодня, ${dateLabel}` : dateLabel}
								</TypographyPrimary>
								{isToday && (
									<Box
										component='img'
										src={verifiedIcon}
										alt=''
										sx={{ width: 16, height: 16, display: 'block' }}
									/>
								)}
							</Box>
						)
					})}
				</Box>
			)}
			{hasAccordion && (
				<Box>
					<Box
						display='flex'
						alignItems='center'
						gap='8px'
						sx={{ cursor: 'pointer', width: 'fit-content' }}
						onClick={() => setIsDescriptionOpen(prev => !prev)}
					>
						<TypographySecondary>Описание схемы лечения</TypographySecondary>
						<Arrow
							stroke={COLORS.secondary}
							style={{
								width: 16,
								height: 16,
								transform: isDescriptionOpen
									? 'rotate(0deg)'
									: 'rotate(180deg)',
							}}
						/>
					</Box>
					<Collapse in={isDescriptionOpen}>
						<Stack gap='16px' mt='8px'>
							{description && (
								<TypographyPrimary
									sx={{
										whiteSpace: 'pre-wrap',
										overflowWrap: 'anywhere',
										wordBreak: 'break-word',
									}}
								>
									{description}
								</TypographyPrimary>
							)}
							{showSummary && totalDuration !== null && (
								<InfoBlock title='Общая продолжительность лечения'>
									{formatTreatmentDuration(totalDuration)}
								</InfoBlock>
							)}
							{milkReject && (
								<InfoBlock title='Забраковать молоко на период'>
									{formatMilkRejectLabel(milkReject)}
								</InfoBlock>
							)}
						</Stack>
					</Collapse>
				</Box>
			)}
		</Stack>
	)
}

export const TreatmentSchemaDetails = ({
	schemas,
}: TreatmentSchemaDetailsProps) => {
	if (!schemas.length) return null

	const totalDuration = getMaxTreatmentDuration(schemas)
	const combinedMilkReject = getCombinedMilkRejectPeriod(schemas)

	return (
		<Stack gap='16px'>
			{schemas.map((schema, index) => {
				const isLast = index === schemas.length - 1
				return (
					<SchemaDetails
						key={schema.id}
						schema={schema}
						totalDuration={totalDuration}
						milkReject={
							isLast
								? combinedMilkReject
								: getMilkRejectPeriod(
										startOfDay(Date.now()),
										schema.milk_reject_days,
									)
						}
						showSummary={isLast}
					/>
				)
			})}
		</Stack>
	)
}
